import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { contacts } from '@repletoryreach/sdk-api';
import { Button, Card, Group, SimpleGrid, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert, DataTable } from '@repletoryreach/sdk-ui/data-display';
import { BarList } from './BarList';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { formatCount, formatDate } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { insightErrorText, leads, productStats, type Row } from '../lib/data';
import { Bullets, MUTED, Muted, Panel, SeeChats, WholeReport, channelLabel } from '../shared';
import type { TabProps } from './tab';

/**
 * What people want: the products and services asked about, what was asked for that the business does not offer, the
 * AI's marketing ideas with their evidence, opportunities, risks and complaints, and the people ready to buy, who can
 * be tagged in the contact book for a broadcast.
 */
export function Demand({ report, rows, onSee }: TabProps) {
  const t = useT();
  const products = productStats(rows);
  const unmet = rows.filter((r) => r.unmet);
  const complaints = rows.filter((r) => r.complaint);
  const hot = leads(rows);
  const [tagging, setTagging] = useState(false);
  const s = report.sections;

  return (
    <Stack gap="lg">
      <Panel title={t('insights.demand.products')} note={t('insights.demand.productsNote')} testId="insights-products">
        {products.length === 0 ? <Muted>{t('insights.demand.noProducts')}</Muted> : (
          <>
            <BarList label={t('insights.demand.products')} items={products.slice(0, 15).map((p) => ({ name: p.name, value: p.count }))} />
            {s && s.demand.length > 0 && (
              <Stack gap="xs">
                <WholeReport t={t} />
                {s.demand.map((d, i) => (
                  <Stack key={i} gap={0}>
                    <Text role="bodySmall" fw={600}>{d.item} · {d.signal}</Text>
                    <Text role="bodySmall" c={MUTED}>{d.detail}</Text>
                    <SeeChats refs={d.refs} onSee={onSee} t={t} />
                  </Stack>
                ))}
              </Stack>
            )}
          </>
        )}
      </Panel>

      {unmet.length > 0 && (
        <Panel title={t('insights.demand.unmet')} note={t('insights.demand.unmetNote')}>
          <Bullets items={unmet.slice(0, 20).map((r) => `${r.unmet} (${r.ref})`)} />
          <SeeChats refs={unmet.map((r) => r.ref)} onSee={onSee} t={t} />
        </Panel>
      )}

      {s && s.marketing.length > 0 && (
        <Stack gap="sm" data-testid="insights-marketing">
          <Panel title={t('insights.demand.marketing')} note={<WholeReport t={t} />}>
            <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
              {s.marketing.map((m, i) => (
                <Card key={i} withBorder>
                  <Stack gap={4}>
                    <Text role="bodySmall" fw={700}>{m.idea}</Text>
                    <Text role="bodySmall">{m.why}</Text>
                    <Text role="caption" c={MUTED}>{t('insights.demand.audience', { value: m.audience })}</Text>
                    <Text role="caption" c={MUTED}>{t('insights.demand.channel', { value: m.channel })}</Text>
                    <Text role="caption" c={MUTED}>{t('insights.demand.timing', { value: m.timing })}</Text>
                    <SeeChats refs={m.refs} onSee={onSee} t={t} />
                  </Stack>
                </Card>
              ))}
            </SimpleGrid>
          </Panel>
        </Stack>
      )}

      {s && (s.opportunities.length > 0 || s.risks.length > 0) && (
        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
          <Panel title={t('insights.demand.opportunities')} note={<WholeReport t={t} />}>
            {s.opportunities.length === 0 ? <Muted>{t('common.none')}</Muted> : s.opportunities.map((o, i) => (
              <Stack key={i} gap={0}>
                <Text role="bodySmall" fw={600}>{o.title}</Text>
                <Text role="bodySmall" c={MUTED}>{o.detail}</Text>
                <SeeChats refs={o.refs} onSee={onSee} t={t} />
              </Stack>
            ))}
          </Panel>
          <Panel title={t('insights.demand.risks')} note={<WholeReport t={t} />}>
            {s.risks.length === 0 ? <Muted>{t('common.none')}</Muted> : s.risks.map((o, i) => (
              <Stack key={i} gap={0}>
                <Text role="bodySmall" fw={600}>{o.title}</Text>
                <Text role="bodySmall" c={MUTED}>{o.detail}</Text>
                <SeeChats refs={o.refs} onSee={onSee} t={t} />
              </Stack>
            ))}
          </Panel>
        </SimpleGrid>
      )}

      {complaints.length > 0 && (
        <Panel title={t('insights.demand.complaints')} note={t('insights.demand.complaintsNote', { count: formatCount(complaints.length) })}>
          <Bullets items={complaints.slice(0, 20).map((r) => `${r.complaint} (${r.ref})`)} />
          <SeeChats refs={complaints.map((r) => r.ref)} onSee={onSee} t={t} />
        </Panel>
      )}

      <Panel title={t('insights.demand.leads')} note={t('insights.demand.leadsNote')} testId="insights-leads"
        actions={hot.length > 0 ? <Button size="xs" onClick={() => setTagging(true)} data-testid="insights-tag">{t('insights.demand.tag')}</Button> : undefined}>
        {hot.length === 0 ? <Muted>{t('insights.demand.noLeads')}</Muted> : (
          <div className="rr-insights__table-scroll">
            <DataTable<Indexed<Row>>
              records={hot as Indexed<Row>[]}
              idAccessor="ref"
              columns={[
                { accessor: 'contact_name', title: t('insights.chats.person'), render: (r) => <Text role="bodySmall" fw={600}>{r.contact_name ?? t('insights.chats.unknown')}</Text> },
                { accessor: 'buying_signal', title: t('insights.demand.signal'), render: (r) => <Text role="caption">{t(`insights.buying.${r.buying_signal}`)}</Text> },
                { accessor: 'buying_detail', title: t('insights.demand.wants'), render: (r) => <Text role="bodySmall">{r.buying_detail ?? r.summary ?? ''}</Text> },
                { accessor: 'at', title: t('insights.chats.date'), render: (r) => <Text role="caption">{formatDate(r.at)}</Text> },
                { accessor: 'channel', title: t('insights.chats.channel'), render: (r) => <Text role="caption">{channelLabel(t, r.channel)}</Text> },
                { accessor: 'conversation_id', title: '', render: (r) => (
                  <Link to={`/inbox?conversation=${encodeURIComponent(r.conversation_id)}`}><Text role="caption">{t('insights.chats.open')}</Text></Link>
                ) }
              ]}
            />
          </div>
        )}
      </Panel>
      <TagModal opened={tagging} onClose={() => setTagging(false)} rows={hot} />
    </Stack>
  );
}

/** Tag the people ready to buy in the contact book, so a broadcast can reach them. Website visitors have no contact. */
function TagModal({ opened, onClose, rows }: { opened: boolean; onClose: () => void; rows: Row[] }) {
  const t = useT();
  const [tag, setTag] = useState('');
  const ids = [...new Set(rows.map((r) => r.contact_id).filter((x): x is string => !!x))];
  const without = rows.filter((r) => !r.contact_id).length;
  const save = useMutation({
    mutationFn: () => contacts.bulkTag({ tag: tag.trim(), action: 'add', contact_ids: ids }),
    onSuccess: (res) => {
      notifySuccess(t('insights.demand.tagged', { count: formatCount(res.data.changed), tag: res.data.tag }), t('insights.demand.tag'));
      onClose();
    }
  });
  return (
    <Modal opened={opened} onClose={onClose} title={t('insights.demand.tagTitle')} size="md">
      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <Stack gap="md" data-testid="insights-tag-modal">
          <Text role="bodySmall">{t('insights.demand.tagIntro', { count: formatCount(ids.length) })}</Text>
          {without > 0 && <Text role="caption" c={MUTED}>{t('insights.demand.tagWithout', { count: formatCount(without) })}</Text>}
          <TextInput label={t('insights.demand.tagName')} description={t('insights.demand.tagHint')} value={tag} maxLength={64} required
            onChange={(e) => setTag(e.currentTarget.value)} data-testid="insights-tag-name" />
          {save.error && <Alert color="error">{insightErrorText(t, save.error)}</Alert>}
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
            <Button type="submit" loading={save.isPending} disabled={tag.trim().length === 0 || ids.length === 0} data-testid="insights-tag-save">
              {t('insights.demand.tagSave', { count: formatCount(ids.length) })}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
